import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixInnovationAssistantPlaygroundV2WixAssistantPlaygroundV2Url(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {};

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ai-site-chat_assistant-training';

/**
 * Creates a query to retrieve a list of messages.
 * The `queryMessages()` function builds a query to retrieve a list of messages and returns a `MessagesQueryBuilder` object.
 * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
 * You can refine the query by chaining `MessagesQueryBuilder` functions onto the query. `MessagesQueryBuilder` functions enable you to sort, filter, and control the results `queryMessages()` returns.
 * `queryMessages()` runs with these `MessagesQueryBuilder` defaults, which you can override:
 *
 * *`skip(0)`
 * * `limit(50)`
 * * `ascending("createdDate")`
 *
 * The functions that are chained to `queryMessages()` are applied in the order they are called. For example, if you apply `ascending('createdDate')` and then `descending('id')`, the results are sorted first by the created date, and then, if there are multiple results with the same created date, the messages are sorted by ID.
 *
 * ## Supported Filters and Sorting
 *
 * | Field | Supported Filters | Sortable  |
 * |----------------|------------------------------------------------------------------------------------------|-----------|
 * | `id`           | [All operators](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#operators). | Sortable  |
 * | `createdDate`  | [All operators](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#operators). | Sortable  |
 * @deprecated Will be removed on 2025-09-01.
 */
export function queryMessages(payload: object): RequestOptionsFactory<any> {
  function __queryMessages({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.innovation.playground.v2.message',
      method: 'POST' as any,
      methodFqn:
        'wix.innovation.assistant.playground.v2.WixAssistantPlaygroundV2.QueryMessages',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInnovationAssistantPlaygroundV2WixAssistantPlaygroundV2Url(
        { protoPath: '/v2/messages/query', data: payload, host }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'messages.createdDate' },
              { path: 'messages.updatedDate' },
              { path: 'messages.feedback.document.updateTs' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'messages.feedback.document.relevance' }],
          },
        ]),
    };

    return metadata;
  }

  return __queryMessages;
}

/**
 * Asks a question to the AI Assistant.
 *
 * This method creates and sends a message from a Wix user.
 * @deprecated Will be removed on 2025-09-01.
 */
export function askQuestion(payload: object): RequestOptionsFactory<any> {
  function __askQuestion({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'question.createdDate' },
          { path: 'question.updatedDate' },
          { path: 'question.feedback.document.updateTs' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [{ path: 'question.feedback.document.relevance' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.innovation.playground.v2.message',
      method: 'POST' as any,
      methodFqn:
        'wix.innovation.assistant.playground.v2.WixAssistantPlaygroundV2.AskQuestion',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInnovationAssistantPlaygroundV2WixAssistantPlaygroundV2Url(
        { protoPath: '/v2/messages/ask-question', data: serializedData, host }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'answer.createdDate' },
              { path: 'answer.updatedDate' },
              { path: 'answer.feedback.document.updateTs' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'answer.feedback.document.relevance' }],
          },
        ]),
    };

    return metadata;
  }

  return __askQuestion;
}

/**
 * Adds feedback to a message.
 * @deprecated Will be removed on 2025-09-01.
 */
export function addFeedback(payload: object): RequestOptionsFactory<any> {
  function __addFeedback({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'feedback.document.updateTs' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [{ path: 'feedback.document.relevance' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.innovation.playground.v2.message',
      method: 'POST' as any,
      methodFqn:
        'wix.innovation.assistant.playground.v2.WixAssistantPlaygroundV2.AddFeedback',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInnovationAssistantPlaygroundV2WixAssistantPlaygroundV2Url(
        { protoPath: '/v2/messages/add-feedback', data: serializedData, host }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'message.createdDate' },
              { path: 'message.updatedDate' },
              { path: 'message.feedback.document.updateTs' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'message.feedback.document.relevance' }],
          },
        ]),
    };

    return metadata;
  }

  return __addFeedback;
}

/**
 * Synchronously updates tags on a list of messages, specified using the messages' IDs.
 *
 * If a tag appears in both `assignTags` and `unassignTags`, it is assigned.
 * @deprecated Will be removed on 2025-09-01.
 */
export function bulkUpdateMessageTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateMessageTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.innovation.playground.v2.message',
      method: 'POST' as any,
      methodFqn:
        'wix.innovation.assistant.playground.v2.WixAssistantPlaygroundV2.BulkUpdateMessageTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInnovationAssistantPlaygroundV2WixAssistantPlaygroundV2Url(
        { protoPath: '/v2/bulk/messages/update-tags', data: payload, host }
      ),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateMessageTags;
}

/**
 * Asynchronously updates tags on a list of messages, specified by applying a filter to all messages. If a filter is not specified, this method updates all messages.
 *
 * If a tag appears in both `assignTags` and `unassignTags`, it is assigned.
 * @deprecated Will be removed on 2025-09-01.
 */
export function bulkUpdateMessageTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateMessageTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.innovation.playground.v2.message',
      method: 'POST' as any,
      methodFqn:
        'wix.innovation.assistant.playground.v2.WixAssistantPlaygroundV2.BulkUpdateMessageTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInnovationAssistantPlaygroundV2WixAssistantPlaygroundV2Url(
        {
          protoPath: '/v2/bulk/messages/update-tags-by-filter',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateMessageTagsByFilter;
}

/**
 * Updates extended fields. This method does not increment the revision.
 * @deprecated Will be removed on 2025-09-01.
 */
export function updateExtendedFields(
  payload: object
): RequestOptionsFactory<any> {
  function __updateExtendedFields({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.innovation.playground.v2.message',
      method: 'POST' as any,
      methodFqn:
        'wix.innovation.assistant.playground.v2.WixAssistantPlaygroundV2.UpdateExtendedFields',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInnovationAssistantPlaygroundV2WixAssistantPlaygroundV2Url(
        {
          protoPath: '/v2/messages/{id}/update-extended-fields',
          data: payload,
          host,
        }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'message.createdDate' },
              { path: 'message.updatedDate' },
              { path: 'message.feedback.document.updateTs' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'message.feedback.document.relevance' }],
          },
        ]),
    };

    return metadata;
  }

  return __updateExtendedFields;
}
